"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";

import { StarIcon } from "@/components/foundations/rating-stars";
import { LINKS } from "@/consts/global";
import type { MenuLinkProps } from "@/types/global";
import { cx } from "@/utils/cx";

type MenuLinkExtraProps = {
	className?: string;
	onClick?: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
	/** When true, show a persistent offset underline on the active route (navbar). */
	activeUnderline?: boolean;
};

const linkClassName = (className?: string, isCurrent?: boolean) =>
	cx(
		"font-display font-semibold",
		isCurrent
			? "underline decoration-current decoration-2 underline-offset-[0.3em]"
			: "md:hover:underline md:hover:decoration-current md:hover:decoration-2 md:hover:underline-offset-[0.3em]",
		className,
	);

function pathMatchesNavSection(pathname: string, href: string) {
	if (href === "/") return pathname === "/";
	return pathname === href || pathname.startsWith(`${href}/`);
}

/** Flip to true when nav leading icons should render. */
const NAV_LINK_LEADING_ICONS_ENABLED = false;

const NAV_LINK_LEADING_ICONS: Partial<Record<string, () => React.ReactNode>> = {
	[LINKS.Favorites.href]: () => (
		<StarIcon progress={100} className="size-4 shrink-0 text-fg-brand-primary" aria-hidden />
	),
};

function NavLinkLabel({ label, href }: { label: string; href: string }) {
	const renderLeadingIcon = NAV_LINK_LEADING_ICONS_ENABLED
		? NAV_LINK_LEADING_ICONS[href]
		: undefined;
	const leadingIcon = renderLeadingIcon?.();

	if (!leadingIcon) {
		return <>{label}</>;
	}

	return (
		<span className="inline-flex items-center gap-1.5">
			{leadingIcon}
			{label}
		</span>
	);
}

/** Footer and other static nav — no `usePathname()` (safe during static prerender). */
function StaticMenuLink({
	label,
	href,
	className,
	onClick,
}: MenuLinkProps & Pick<MenuLinkExtraProps, "className" | "onClick">) {
	return (
		<Link href={href} className={linkClassName(className)} onClick={onClick}>
			<NavLinkLabel label={label} href={href} />
		</Link>
	);
}

function ActiveMenuLink({
	label,
	href,
	className,
	onClick,
}: MenuLinkProps & Pick<MenuLinkExtraProps, "className" | "onClick">) {
	const pathname = usePathname();
	const isCurrent = pathMatchesNavSection(pathname, href);

	return (
		<Link
			href={href}
			aria-current={isCurrent ? "page" : undefined}
			className={linkClassName(className, isCurrent)}
			onClick={onClick}
		>
			<NavLinkLabel label={label} href={href} />
		</Link>
	);
}

export default function MenuLink({
	className,
	onClick,
	activeUnderline,
	label,
	href,
}: MenuLinkProps & MenuLinkExtraProps) {
	if (!href) return null;

	if (activeUnderline) {
		return <ActiveMenuLink label={label} href={href} className={className} onClick={onClick} />;
	}

	return <StaticMenuLink label={label} href={href} className={className} onClick={onClick} />;
}
